import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 w-full transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-900/5" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0">
            <img src="/images/logo.jpeg" alt="Vision Building Systems logo" className="h-10 w-auto" />
            <span className="hidden md:block leading-tight">
              <span className="block font-heading font-800 font-extrabold text-[#00203d] text-lg tracking-tight">
                Vision Building Systems
              </span>
              <span className="block text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold">
                Modular Construction
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
                    isActive
                      ? "text-[#00203d] bg-slate-100"
                      : "text-slate-600 hover:text-[#00203d] hover:bg-slate-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 text-sm font-bold text-[#00203d] hover:text-amber-600 transition-colors duration-200"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              {SITE.phone}
            </a>
            <Link
              to="/contact"
              data-testid="nav-quote-button"
              className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold px-5 py-2.5 rounded-md transition-colors duration-200"
            >
              Get a Quote
            </Link>
          </div>

          <button
            type="button"
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-md text-[#00203d] hover:bg-slate-100"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="nav-mobile-menu"
          aria-label="Mobile"
          className="lg:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-1 shadow-xl"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`${link.testId}-mobile`}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-md font-semibold ${
                  isActive ? "bg-slate-100 text-[#00203d]" : "text-slate-600"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <a
            href={SITE.phoneHref}
            data-testid="nav-phone-link-mobile"
            className="flex items-center gap-2 px-4 py-3 font-bold text-[#00203d]"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-quote-button-mobile"
            className="block text-center bg-amber-600 text-white font-bold px-4 py-3 rounded-md"
          >
            Get a Quote
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
